// Device-level accessibility settings — apply to THIS device regardless of
// which child profile is active (reduce-motion / high-contrast are about the
// person looking at the screen, not the learner). Kept out of per-child
// Progress on purpose; persisted in localStorage. Applied as classes on
// <html> so they cover the whole app (every `.sb-root` subtree included).
import { useSyncExternalStore } from "react";

interface DeviceSettings {
  reduceMotion: boolean;
  highContrast: boolean;
  /** MA-277 — narration playback rate for the STORY READER only (see
   *  src/engine/narrationPacing.ts's effectiveNarrationRate). `null` = unset,
   *  meaning "use today's default pace" (DEFAULT_NARRATION_RATE) — never
   *  silently regress an installed base's pace just because this field now
   *  exists. Only the two literal values the Settings row offers are valid;
   *  anything else parses back to `null`, never an arbitrary rate. */
  narrationRate: number | null;
}
export const DEFAULT_DEVICE_SETTINGS: DeviceSettings = {
  reduceMotion: false,
  highContrast: false,
  narrationRate: null,
};
const KEY = "pencilwise-device-settings";

/** The only two rates the Settings row exposes (MA-277). */
export const NARRATION_RATE_OPTIONS = [0.75, 0.9] as const;

/** Tolerant parse — unknown/corrupt input falls back to defaults (never throws). */
export function parseDeviceSettings(raw: string | null | undefined): DeviceSettings {
  if (!raw) return { ...DEFAULT_DEVICE_SETTINGS };
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    const narrationRate = (NARRATION_RATE_OPTIONS as readonly unknown[]).includes(o?.narrationRate)
      ? (o.narrationRate as number)
      : null;
    return { reduceMotion: o?.reduceMotion === true, highContrast: o?.highContrast === true, narrationRate };
  } catch {
    return { ...DEFAULT_DEVICE_SETTINGS };
  }
}
export function serializeDeviceSettings(s: DeviceSettings): string {
  const narrationRate = (NARRATION_RATE_OPTIONS as readonly unknown[]).includes(s.narrationRate)
    ? s.narrationRate
    : null;
  return JSON.stringify({ reduceMotion: !!s.reduceMotion, highContrast: !!s.highContrast, narrationRate });
}
/** Pure: settings -> the <html> classes that express them (unit-tested). */
export function deviceSettingsClassList(s: DeviceSettings): string[] {
  const c: string[] = [];
  if (s.reduceMotion) c.push("reduce-motion");
  if (s.highContrast) c.push("high-contrast");
  return c;
}

function readStorage(): DeviceSettings {
  if (typeof localStorage === "undefined") return { ...DEFAULT_DEVICE_SETTINGS };
  return parseDeviceSettings(localStorage.getItem(KEY));
}

let snapshot: DeviceSettings = readStorage();
const listeners = new Set<() => void>();

function applyToDocument(s: DeviceSettings): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  for (const cls of ["reduce-motion", "high-contrast"]) el.classList.remove(cls);
  for (const cls of deviceSettingsClassList(s)) el.classList.add(cls);
}

function refresh(): void {
  snapshot = readStorage();
  applyToDocument(snapshot);
  for (const l of listeners) l();
}

function getDeviceSettings(): DeviceSettings {
  return snapshot;
}
export function setDeviceSetting<K extends keyof DeviceSettings>(key: K, value: DeviceSettings[K]): void {
  const next = { ...readStorage(), [key]: value };
  try {
    localStorage.setItem(KEY, serializeDeviceSettings(next));
  } catch {
    /* private mode / quota — keep the in-memory snapshot in sync anyway */
  }
  refresh();
}

/** Call ONCE at app start (main.jsx) — applies persisted classes before first
 *  paint (no flash) and keeps them in sync across tabs. */
export function initDeviceSettings(): void {
  snapshot = readStorage();
  applyToDocument(snapshot);
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
      if (e.key === KEY) refresh();
    });
  }
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
/** React binding for the Settings toggles. */
export function useDeviceSettings(): DeviceSettings {
  return useSyncExternalStore(subscribe, getDeviceSettings, () => DEFAULT_DEVICE_SETTINGS);
}
